/**
 * Proposta em PDF (17 §3.2 aba 5; D103), no modelo atual da empresa (proposta PORTODAFOLHA-11-2025):
 * cabeçalho com a marca, dados da empresa e do processo, itens com marca e modelo, declarações, conta bancária,
 * representante legal e assinatura.
 * Só a versão readequada (D111): itens ganhos, pelo nosso lance final. A proposta inicial deixou de existir
 * (o preço inicial é lançado direto no portal); as propostas iniciais já geradas continuam no histórico.
 * Cada PDF gerado fica nos arquivos da licitação e na tabela proposta, com a foto do que saiu nele.
 */
import { useEffect, useMemo, useState } from "react";
import { useGetList, useGetOne, useNotify, usePermissions, useRefresh } from "react-admin";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PdfIcon from "@mui/icons-material/PictureAsPdfOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import type { Permissoes } from "../lib/provedores";
import { formatarDataHora, formatarMoeda, formatarNumero } from "../lib/formato";
import { supabase } from "../lib/supabase";
import {
  diasPorExtenso,
  documentoProposta,
  imagemComoDataUrl,
  pdfDaProposta,
  type ConteudoPdf,
  type Empresa,
  type LicitacaoProposta,
  type LinhaProposta,
  type ModeloProposta,
  type Orgao,
  type PrecoGravado,
  type Versao,
} from "../lib/proposta-pdf";
import { DialogoConta, textoConta, type ContaBancaria } from "./contas-bancarias";
import { DialogoProdutoRapido } from "./produtos";
import type { ItemLicitacao } from "./licitacao-itens";

/* ------------------------------------ Dados ------------------------------------ */

/** Monta e gera o PDF (logo do site e assinatura cadastrada em Dados da empresa). */
async function gerarPdf(c: ConteudoPdf): Promise<Blob> {
  const logo = await fetch("/logo-horizontal.png")
    .then((r) => (r.ok ? r.blob() : null))
    .then((b) => (b ? imagemComoDataUrl(b) : null))
    .catch(() => null);
  let assinatura: string | null = null;
  if (c.empresa.assinatura_caminho) {
    const { data } = await supabase.storage.from("empresa").download(c.empresa.assinatura_caminho);
    if (data) assinatura = await imagemComoDataUrl(data).catch(() => null);
  }
  return pdfDaProposta(documentoProposta(c, logo, assinatura));
}

/* ------------------------------------ Aba Proposta ------------------------------------ */

export function PropostaLicitacao({ licitacao }: { licitacao: LicitacaoProposta }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const versao: Versao = "readequada";
  const [contaId, setContaId] = useState<string>("");
  const [validade, setValidade] = useState("");
  const [entrega, setEntrega] = useState("");
  const [pagamento, setPagamento] = useState("");
  const [gerando, setGerando] = useState(false);
  const [precos, setPrecos] = useState<PrecoGravado[] | null>(null);
  const [lances, setLances] = useState<Map<string, { situacao: string; lance: number | null }>>(new Map());
  // Correções feitas no próprio aviso (D118): produto, qualificação do representante e conta bancária.
  const [releitura, setReleitura] = useState(0);
  const [produtoEditado, setProdutoEditado] = useState<string | null>(null);
  const [editandoQualificacao, setEditandoQualificacao] = useState(false);
  const [novaConta, setNovaConta] = useState(false);
  const { permissions } = usePermissions<Permissoes | null>();
  const admin = permissions?.papel === "admin";

  const { data: itens = [] } = useGetList<ItemLicitacao>("licitacao_item", {
    filter: { licitacao_id: licitacao.id },
    pagination: { page: 1, perPage: 2000 },
    sort: { field: "numero", order: "ASC" },
  });
  const { data: contas = [], refetch: relerContas } = useGetList<ContaBancaria>("conta_bancaria", { filter: { ativo: true }, pagination: { page: 1, perPage: 50 }, sort: { field: "banco", order: "ASC" } });
  const { data: modelos = [], refetch: relerModelos } = useGetList<ModeloProposta>("modelo_proposta", { pagination: { page: 1, perPage: 1 }, sort: { field: "criado_em", order: "ASC" } });
  const { data: empresas = [] } = useGetList<Empresa & { id: string }>("empresa", { pagination: { page: 1, perPage: 1 }, sort: { field: "id", order: "ASC" } });
  const { data: orgao } = useGetOne<Orgao & { id: string }>("pessoa", { id: licitacao.pessoa_id });
  const { data: modalidade } = useGetOne<{ id: string; nome: string }>("modalidade", { id: licitacao.modalidade_id ?? "" }, { enabled: Boolean(licitacao.modalidade_id) });
  const { data: geradas = [] } = useGetList<{ id: string; versao: string; total: number; criado_em: string; arquivo_id: string | null }>("proposta", {
    filter: { licitacao_id: licitacao.id },
    pagination: { page: 1, perPage: 50 },
    sort: { field: "criado_em", order: "DESC" },
  });
  const modelo = modelos[0];
  const empresa = empresas[0];

  // Conta principal e prazos da licitação como padrão.
  useEffect(() => {
    if (!contaId && contas.length) setContaId(contas.find((c) => c.principal)?.id ?? contas[0]?.id ?? "");
  }, [contas.length]);
  useEffect(() => {
    if (modelo && !validade) setValidade(String(modelo.validade_dias));
  }, [modelo?.id]);
  useEffect(() => {
    setEntrega(licitacao.prazo_entrega_dias ? `em até ${diasPorExtenso(licitacao.prazo_entrega_dias)}, conforme estipulado no Edital` : "Conforme estipulado no Edital");
    setPagamento(
      licitacao.prazo_pagamento_dias
        ? `no prazo máximo de até ${diasPorExtenso(licitacao.prazo_pagamento_dias)}, contados a partir do recebimento da Nota Fiscal ou Fatura`
        : "conforme estipulado no Edital",
    );
  }, [licitacao.prazo_entrega_dias, licitacao.prazo_pagamento_dias]);

  const ids = itens.map((i) => i.id).join(",");
  useEffect(() => {
    if (!ids) {
      setPrecos([]);
      return;
    }
    let ativo = true;
    const lista = ids.split(",");
    void Promise.all([
      supabase.from("item_precificacao").select("item_id, versao, momento, preco_unit, produto_id, produto:produto_id(descricao, fabricante, modelo)").in("item_id", lista).order("versao", { ascending: false }),
      supabase.from("item_resultado").select("item_id, situacao, nosso_lance_final").in("item_id", lista),
    ]).then(([p, r]) => {
      if (!ativo) return;
      setPrecos((p.data ?? []) as unknown as PrecoGravado[]);
      setLances(new Map(((r.data ?? []) as { item_id: string; situacao: string; nosso_lance_final: number | null }[]).map((x) => [x.item_id, { situacao: x.situacao, lance: x.nosso_lance_final }])));
    });
    return () => {
      ativo = false;
    };
  }, [ids, releitura]);

  const ordenados = useMemo(
    () => [...itens].sort((a, b) => (a.lote ?? "").localeCompare(b.lote ?? "", "pt-BR", { numeric: true }) || a.numero - b.numero),
    [itens],
  );

  // Linhas da versão escolhida e os avisos do que falta.
  const { linhas, avisos } = useMemo(() => {
    const linhas: LinhaProposta[] = [];
    const avisos: { texto: string; produtoId?: string }[] = [];
    for (const i of ordenados) {
      if (!i.participar) continue;
      const doItem = (precos ?? []).filter((p) => p.item_id === i.id);
      const rotulo = `${i.lote ? `${i.lote}.` : ""}${i.numero}`;
      const r = lances.get(i.id);
      if (r?.situacao !== "ganho") continue;
      const base: PrecoGravado | undefined = doItem[0];
      const preco = r.lance !== null ? Number(r.lance) : base ? Number(base.preco_unit) : null;
      if (r.lance === null) avisos.push({ texto: `Item ${rotulo}: ganho sem lance final registrado (usei o último preço gravado).` });
      if (preco === null) continue;
      if (!base?.produto?.fabricante || !base.produto.modelo)
        avisos.push({ texto: `Item ${rotulo}: produto sem marca ou modelo cadastrados.`, ...(base?.produto_id ? { produtoId: base.produto_id } : {}) });
      const quantidade = Number(i.qtd_max);
      linhas.push({
        item: rotulo,
        quantidade,
        unidade: i.unidade ?? "",
        descricao: i.descricao,
        marca: base?.produto?.fabricante ?? "",
        modelo: base?.produto?.modelo ?? "",
        valor: preco,
        total: Math.round(preco * quantidade * 100) / 100,
      });
    }
    if (linhas.length === 0) avisos.push({ texto: "Nenhum item ganho com resultado registrado na aba Disputa." });
    return { linhas, avisos };
  }, [ordenados, precos, lances]);

  const total = linhas.reduce((s, l) => s + l.total, 0);
  const conta = contas.find((c) => c.id === contaId) ?? null;
  const validadeDias = Number(validade);

  function declaracoes(): string[] {
    if (!modelo) return [];
    return modelo.declaracoes
      .replaceAll("{validade}", diasPorExtenso(validadeDias))
      .replaceAll("{entrega}", entrega.trim())
      .replaceAll("{pagamento}", pagamento.trim())
      .split(/\n+/)
      .map((t) => t.trim())
      .filter(Boolean);
  }

  async function gerar() {
    if (!empresa || !orgao || !modelo) return;
    setGerando(true);
    try {
      const data = new Date();
      const conteudo: ConteudoPdf = {
        empresa,
        orgao,
        modalidade: modalidade?.nome ?? null,
        licitacao,
        linhas,
        total: Math.round(total * 100) / 100,
        declaracoes: declaracoes(),
        conta: conta ? { texto: textoConta(conta), pix: conta.pix } : null,
        modelo,
        data,
        versao,
      };
      const blob = await gerarPdf(conteudo);
      const nome = `Proposta readequada ${licitacao.numero.replace(/\//g, "-")} - Sobral Nunes.pdf`;
      const caminho = `${licitacao.id}/${data.toISOString().replace(/[:.]/g, "-")}-proposta.pdf`;
      const { error: erroUpload } = await supabase.storage.from("licitacoes").upload(caminho, blob, { contentType: "application/pdf", upsert: false });
      if (erroUpload) throw new Error(erroUpload.message);
      const { data: arquivo, error: erroArquivo } = await supabase
        .from("licitacao_arquivo")
        .insert({ licitacao_id: licitacao.id, tipo: "proposta", arquivo_caminho: caminho, arquivo_nome: nome })
        .select("id")
        .single();
      if (erroArquivo) throw new Error(erroArquivo.message);
      const { error } = await supabase.from("proposta").insert({
        licitacao_id: licitacao.id,
        versao,
        conta_bancaria_id: conta?.id ?? null,
        total: conteudo.total,
        dados: { ...conteudo, data: data.toISOString(), empresa: { ...empresa, assinatura_caminho: undefined } },
        arquivo_id: (arquivo as { id: string }).id,
      });
      if (error) throw new Error(error.message);
      // Baixa na hora.
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = nome;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      notify("Proposta gerada e guardada nos arquivos da licitação.", { type: "success" });
      refresh();
    } catch (e) {
      notify(`Não foi possível gerar a proposta: ${(e as Error).message}`, { type: "error" });
    } finally {
      setGerando(false);
    }
  }

  async function abrirGerada(arquivoId: string | null) {
    if (!arquivoId) return;
    const { data } = await supabase.from("licitacao_arquivo").select("arquivo_caminho").eq("id", arquivoId).single();
    const caminho = (data as { arquivo_caminho: string } | null)?.arquivo_caminho;
    if (!caminho) return;
    const { data: assinada } = await supabase.storage.from("licitacoes").createSignedUrl(caminho, 300);
    if (assinada) window.open(assinada.signedUrl, "_blank", "noopener");
  }

  if (!modelo || !empresa) return <Typography variant="body2">Carregando…</Typography>;
  const validadeInvalida = !Number.isInteger(validadeDias) || validadeDias <= 0;

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
          Proposta readequada (itens ganhos)
        </Typography>
        <TextField select size="small" label="Conta bancária" value={contaId} onChange={(e) => setContaId(e.target.value)} sx={{ minWidth: 320 }}>
          {contas.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {textoConta(c)}
              {c.principal ? " · principal" : ""}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 3, md: 2 }}>
          <TextField
            size="small"
            label="Validade (dias)"
            value={validade}
            onChange={(e) => setValidade(e.target.value.replace(/\D/g, ""))}
            error={validadeInvalida}
            fullWidth
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 9, md: 5 }}>
          <TextField size="small" label="Condições de entrega" value={entrega} onChange={(e) => setEntrega(e.target.value)} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <TextField size="small" label="Condições de pagamento" value={pagamento} onChange={(e) => setPagamento(e.target.value)} fullWidth />
        </Grid>
      </Grid>

      {contas.length === 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            admin ? (
              <Button color="inherit" size="small" onClick={() => setNovaConta(true)}>
                Cadastrar conta
              </Button>
            ) : undefined
          }
        >
          Nenhuma conta bancária cadastrada: a proposta sai sem dados bancários.{admin ? "" : " Peça ao administrador para cadastrar."}
        </Alert>
      )}
      {!modelo.representante_qualificacao && (
        <Alert
          severity="info"
          sx={{ mb: 1 }}
          action={
            admin ? (
              <Button color="inherit" size="small" onClick={() => setEditandoQualificacao(true)}>
                Preencher
              </Button>
            ) : undefined
          }
        >
          A qualificação do representante legal ainda não foi preenchida: a proposta sai só com o nome e o cargo.{admin ? "" : " Peça ao administrador para preencher."}
        </Alert>
      )}
      {avisos.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {avisos.map((a) => (
            <Stack key={a.texto} direction="row" spacing={0.5} alignItems="center">
              <span>{a.texto}</span>
              {a.produtoId && (
                <Tooltip title="Editar o produto (marca, modelo)">
                  <IconButton size="small" color="inherit" onClick={() => setProdutoEditado(a.produtoId ?? null)} aria-label="Editar produto">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          ))}
        </Alert>
      )}
      {produtoEditado && (
        <DialogoProdutoRapido
          produtoId={produtoEditado}
          textoInicial=""
          unidadeItem={null}
          aoFechar={() => setProdutoEditado(null)}
          aoCriar={() => {
            setProdutoEditado(null);
            setReleitura((v) => v + 1);
          }}
        />
      )}
      {editandoQualificacao && (
        <DialogoQualificacao
          modeloId={modelo.id}
          aoFechar={() => setEditandoQualificacao(false)}
          aoGravar={() => {
            setEditandoQualificacao(false);
            void relerModelos();
          }}
        />
      )}
      {novaConta && (
        <DialogoConta
          conta={null}
          primeira
          aoFechar={() => setNovaConta(false)}
          aoGravar={() => {
            setNovaConta(false);
            void relerContas();
          }}
        />
      )}

      <TableContainer sx={{ overflowX: "auto", mb: 2 }}>
        <Table size="small" sx={{ minWidth: 820 }}>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Quant.</TableCell>
              <TableCell>Unid.</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Marca</TableCell>
              <TableCell>Modelo</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {linhas.map((l) => (
              <TableRow key={l.item}>
                <TableCell>{l.item}</TableCell>
                <TableCell align="right">{formatarNumero(l.quantidade)}</TableCell>
                <TableCell>{l.unidade}</TableCell>
                <TableCell sx={{ maxWidth: 380 }}>
                  <Typography variant="caption" sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {l.descricao}
                  </Typography>
                </TableCell>
                <TableCell sx={{ color: l.marca ? undefined : "error.main" }}>{l.marca || "—"}</TableCell>
                <TableCell sx={{ color: l.modelo ? undefined : "error.main" }}>{l.modelo || "—"}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {formatarMoeda(l.valor)}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {formatarMoeda(l.total)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={7} align="right" sx={{ fontWeight: 700 }}>
                Total
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                {formatarMoeda(Math.round(total * 100) / 100)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={1} alignItems="center">
        <Button variant="contained" startIcon={<PdfIcon />} disabled={gerando || linhas.length === 0 || validadeInvalida || !orgao} onClick={() => void gerar()}>
          {gerando ? "Gerando…" : "Gerar PDF da proposta"}
        </Button>
        <Typography variant="caption" color="text.secondary">
          O PDF baixa na hora e fica guardado nos arquivos da licitação. A assinatura digital (certificado) é feita depois, no PDF.
        </Typography>
      </Stack>

      {geradas.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Propostas geradas
          </Typography>
          <Table size="small">
            <TableBody>
              {geradas.map((g) => (
                <TableRow key={g.id}>
                  <TableCell>{g.versao === "readequada" ? "Readequada" : "Inicial"}</TableCell>
                  <TableCell>{formatarDataHora(g.criado_em)}</TableCell>
                  <TableCell align="right">{formatarMoeda(g.total)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<PdfIcon />} onClick={() => void abrirGerada(g.arquivo_id)} disabled={!g.arquivo_id}>
                      Abrir
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

/** Qualificação do representante legal (modelo da proposta), preenchida no próprio aviso. */
function DialogoQualificacao({ modeloId, aoFechar, aoGravar }: { modeloId: string; aoFechar: () => void; aoGravar: () => void }) {
  const notify = useNotify();
  const [texto, setTexto] = useState("");
  const [gravando, setGravando] = useState(false);
  async function gravar() {
    setGravando(true);
    const { error } = await supabase.from("modelo_proposta").update({ representante_qualificacao: texto.trim() }).eq("id", modeloId);
    setGravando(false);
    if (error) notify(`Não foi possível gravar: ${error.message}`, { type: "error" });
    else {
      notify("Qualificação do representante gravada no modelo da proposta.", { type: "success" });
      aoGravar();
    }
  }
  return (
    <Dialog open onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="sm">
      <DialogTitle>Qualificação do representante legal</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Vale para todas as propostas (o mesmo campo de Parâmetros do sistema → Modelo da proposta).
        </Typography>
        <TextField
          autoFocus
          label="Qualificação do representante legal"
          helperText="Como sai na proposta: nome, nacionalidade, estado civil, profissão, documentos e endereço."
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          fullWidth
          multiline
          minRows={3}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void gravar()} disabled={gravando || !texto.trim()}>
          {gravando ? "Gravando…" : "Gravar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
